import { UserPlusIcon } from "@heroicons/react/24/outline";
import { SidebarItem, SidebarLabel } from "components/layout/sidebar";
import { trpcClient } from "components/rpc/trpc";
import { fancyChoice } from "components/ui/fancy-choice";
import { PrimaryButton } from "components/ui/primary-button";
import { useCreateClientMutation } from "generated/graphql";
import React, { MouseEventHandler, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useCrmMode, useRegionId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";

const useAddNewClientAction = (
    source: string,
): [boolean, MouseEventHandler<HTMLButtonElement>] => {
    const { push } = useHistory();
    const regionId = useRegionId();
    const { mode } = useCrmMode();
    const isAvailable = !!regionId && mode !== "none";
    const createClient = useCreateClientMutation();
    const callback = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            if (e.preventDefault) {
                e.preventDefault();
            }
            if (!regionId) return;

            const createPlacementClient = async () => {
                handleEvent("create-client", { source });
                const response = await createClient.mutateAsync({ regionId });
                if (response?.createClient?.id) {
                    push("/clients/show/" + response.createClient.id);
                }
            };
            const createHomeCareClient = async () => {
                handleEvent("create-home-care-client", { source });
                const result = await trpcClient.homeCareLead.create.mutate({
                    name: "",
                    regionId,
                });
                push(`/home-care-clients/show/${result.id}`);
            };

            if (mode === "home-care-only") {
                await createHomeCareClient();
            } else if (mode === "placement-only") {
                await createPlacementClient();
            } else if (mode === "combined") {
                const choice = await fancyChoice<"placement" | "homeCare" | "cancel">({
                    title: "New client",
                    prompt: "Which kind of client would you like to create?",
                    actions: [
                        {
                            label: "Placement client",
                            value: "placement",
                            style: "secondary",
                        },
                        {
                            label: "Home care client",
                            value: "homeCare",
                            style: "secondary",
                        },
                        { label: "Cancel", value: "cancel", style: "subtle" },
                    ],
                });
                if (choice === "placement") await createPlacementClient();
                else if (choice === "homeCare") await createHomeCareClient();
            }
        },
        [regionId, mode, createClient, push, source],
    );
    return [isAvailable, callback];
};

export const AddClientButton: React.FC<unknown> = () => {
    const [isAvailable, onClick] = useAddNewClientAction("button");
    if (!isAvailable) {
        return null;
    }
    return (
        <PrimaryButton onClick={onClick}>
            <div className="flex items-center space-x-2">
                <UserPlusIcon className="w-5 h-5" />
                <span className="text-sm">New client</span>
            </div>
        </PrimaryButton>
    );
};

export const AddClientIcon: React.FC<unknown> = () => {
    const [isAvailable, onClick] = useAddNewClientAction("topbar");
    if (!isAvailable) {
        return null;
    }
    return (
        <div className="new-client-button">
            <button
                key="headerButton"
                className="rounded-full p-1 flex items-center text-gray-600 hover:text-gray-900 hover:bg-brand-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-brand-500"
                onClick={onClick}
                title="New client">
                <UserPlusIcon className="w-6 h-6" />
            </button>
        </div>
    );
};

export const AddClientSidebar: React.FC<{ mode: "icons" | "full" }> = ({ mode }) => {
    const [isAvailable, onClick] = useAddNewClientAction("sidebar");
    if (!isAvailable) {
        return null;
    }

    if (mode === "icons") {
        return (
            <button
                onClick={onClick}
                className="p-2 rounded-lg text-brand-800 hover:bg-zinc-950/5 dark:text-brand-400 dark:hover:bg-brand-950/30"
                title="New Client">
                <UserPlusIcon className="size-5" />
            </button>
        );
    }

    return (
        <SidebarItem current={false} onClick={onClick}>
            <UserPlusIcon />
            <SidebarLabel>New Client</SidebarLabel>
        </SidebarItem>
    );
};
